import {
    AccountInfo,
    AuthenticationResult,
    AuthError,
    BrowserAuthError,
    BrowserAuthErrorCodes,
    EventMessage,
    EventType,
    InteractionRequiredAuthError,
    IPublicClientApplication,
    LogLevel,
    PublicClientApplication
} from '@azure/msal-browser';
import _ from 'lodash';
import { AccessGroup, clearAttributesApiCache, getAccessGroups, getMyPermissions } from '../helpers/api';
import { AzureConfig } from '@/appConfig.ts';
import { AxiosInstance } from 'axios';
import { SessionStoreCache } from '@/shared/services/sessionStoreCache.ts';

const sessionStorageAccessGroupsKey = 'cl_accessgroups';
const sessionStoragePermissionsKey = 'cl_permissions';

/**
 * The roles that on their own grant access to Common Library.
 *
 * Roles that only enable a specific integration or feature (`OasisDataSource`, `PimsDcDataSource`,
 * `SymbolEditor` and `TypeEditor`) are deliberately left out, as they do not grant general access.
 */
export const commonLibraryAccessRoles = [
    'Administrator',
    'LibraryEditor',
    'CodeEditor',
    'Viewer',
    'ExternallyAuthorizedEditor'
];

/**
 * Errors from MSAL's hidden-iframe token renewal that mean the iframe never delivered a result, typically because
 * the page served at the redirect URI did not hand the response back to the main frame (see issue #3428).
 * A full-page redirect to Entra ID still works in these cases.
 */
const silentRenewalFailureCodes: string[] = [
    BrowserAuthErrorCodes.timedOut,
    BrowserAuthErrorCodes.iframeClosedPrematurely,
    BrowserAuthErrorCodes.redirectBridgeEmptyResponse
];

export function canBeResolvedByRedirect(error: unknown): boolean {
    if (error instanceof InteractionRequiredAuthError) {
        return true;
    }
    return error instanceof BrowserAuthError && silentRenewalFailureCodes.includes(error.errorCode);
}

/**
 * True when an error means that no access token could be acquired, either an MSAL error itself or the error
 * `acquireToken` throws after a redirect that did not navigate away.
 */
export function isAuthenticationFailure(error: unknown): boolean {
    return error instanceof AuthError || (error instanceof Error && error.cause instanceof AuthError);
}

class AuthService {
    authConfig: AzureConfig;
    $http: AxiosInstance;
    pca: IPublicClientApplication;

    constructor($http: AxiosInstance, config: AzureConfig | undefined) {
        if (!config) {
            throw new Error('Azure config is not provided');
        }
        this.authConfig = config;
        this.$http = $http;

        this.pca = new PublicClientApplication({
            auth: {
                clientId: config.clientId,
                authority: config.instance + config.tenant,
                redirectUri: config.redirectUri,
                postLogoutRedirectUri: config.redirectUri

            },
            cache: {
                cacheLocation: 'sessionStorage'
            },
            system: {
                loggerOptions: {
                    loggerCallback(_, message) {
                        /* eslint-disable-next-line no-console */
                        console.error(message);
                    },
                    logLevel: LogLevel.Error
                }
            }
        });

        this.pca.addEventCallback((event: EventMessage) => {
            // this callback will typically only be executed when opening a new tab, not when refreshing the page
            if (event.payload) {
                const payload = event.payload as AccountInfo;
                this.pca.setActiveAccount(payload);
            }
        }, [EventType.LOGIN_SUCCESS]);
    }

    async init(): Promise<void> {
        await this.pca.initialize();
    }

    /**
     * Awaits for the login redirect to complete.
     */
    async redirectHandled(): Promise<AuthenticationResult | null> {
        return await this.pca.handleRedirectPromise();
    }

    /**
     * Checks if there is a user profile signed in, if it not exists,
     * then assumes that the user has not signed in.
     */
    isSignedIn(): boolean {
        return !!this.getUser();
    }

    /**
     * Signs in a user and set the signed in user as active account.
     */
    async signIn(): Promise<void> {
        const accounts = this.pca.getAllAccounts();
        if (accounts.length > 0) {
            this.pca.setActiveAccount(accounts[0]);
        }

        const redirectHandled = await this.pca.handleRedirectPromise();

        if (redirectHandled === null && (!accounts || accounts.length === 0)) {
            const loginRequest = { scopes: ['User.Read'] };
            await this.pca.loginRedirect(loginRequest);
        }
    }

    /**
     * SignsOut the user, clears the `sessionStorage` and removes the users cached attributes from the API cache.
     */
    async signOut(): Promise<void> {
        await clearAttributesApiCache(this);
        sessionStorage.clear();
        await this.pca.logoutRedirect();
        window.location.reload();
    }

    /**
     * Returns the active account from MSAL
     */
    getUser(): AccountInfo | null {
        return this.pca.getActiveAccount() ?? null;
    }

    /**
     * Returns the roles assigned to the active user, as found in the claims of the id token.
     */
    getUserRoles(): string[] {
        return this.getUser()?.idTokenClaims?.roles ?? [];
    }

    /**
     * Evaluates if the user has at least one role granting access to Common Library.
     *
     * This is evaluated from the role claims in the id token instead of the permissions from the API, because
     * the `CanRead` permission is currently an alias for `IsAuthenticatedUser` in the backend, and is therefore
     * granted to every authenticated user regardless of their roles.
     * Once the backend enforces a real `CanRead` requirement, this check should be replaced by
     * `hasPermission('CanRead')` and the role list above should be removed.
     */
    hasAnyCommonLibraryRole(): boolean {
        return this.getUserRoles().some(role => commonLibraryAccessRoles.includes(role));
    }

    /**
     * Requests an accessToken from Azure Entra ID (Azure Ad).
     * Awaits for Redirect login to be done, before it attempts to acquire an access token silently.
     *
     * If the RefreshToken is expired, an access token will be requested silently in a hidden iframe.
     * This also means that the UI hosting server CANNOT serve the header `X-Frame-Options: Deny` to it's clients.
     * The hidden iframe lands on the redirect URI, which the UI host must answer with the redirect-bridge page
     * (`signin-oidc.html`) rather than the application itself. If the iframe still fails to deliver a token, the
     * user is sent through a full-page redirect instead of being left without a token.
     *
     * MSAL Docs: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/
     */
    async acquireToken(): Promise<string> {
        await this.pca.handleRedirectPromise();

        const accessRequest = {
            scopes: [`${this.authConfig.clientId}/user_impersonation`]
        };

        try {
            const silentTokenResponse = await this.pca.acquireTokenSilent(accessRequest);
            return silentTokenResponse.accessToken;
        } catch (authError) {
            if (canBeResolvedByRedirect(authError)) {
                await this.pca.acquireTokenRedirect(accessRequest);
                // Reaching this point means the redirect never navigated away, so no token can be returned.
                throw new Error('Redirect completed without navigation', { cause: authError });
            } else {
                throw authError;
            }
        }
    }

    /**
     * Fetches, caches and returns an array of the users permissions.
     *
     * When the permissions cannot be fetched because no access token could be acquired, the error is rethrown:
     * an authentication failure must not be presented to the user as "no permissions" (see issue #3428).
     *
     * @returns {Promise<string[]>}
     */
    async getPermissions(): Promise<string[]> {
        const permissions = SessionStoreCache.readFromSessionStorage(sessionStoragePermissionsKey);

        if (!permissions || permissions.isExpired) {
            try {
                const permissionResponse = await getMyPermissions(this);
                const permissionsCache = new SessionStoreCache(permissionResponse);
                permissionsCache.writeToSessionStorage(sessionStoragePermissionsKey);

                return permissionsCache.values;
            } catch (e) {
                if (isAuthenticationFailure(e)) {
                    throw e;
                }
                const permissionsCache = new SessionStoreCache([], -1); // set to expired to trigger a retry on next page load
                permissionsCache.writeToSessionStorage(sessionStoragePermissionsKey);

                return permissionsCache.values;
            }
        }

        return permissions.values;
    }

    /**
     * Fetches the users AccessGroups and caches them in SessionStorage.
     */
    async getAccessGroups(): Promise<string[]> {
        const accessGroups = SessionStoreCache.readFromSessionStorage(sessionStorageAccessGroupsKey);

        if (!accessGroups || accessGroups.isExpired) {
            try {
                const agResponse = await getAccessGroups(this);
                const groupsForUser = this.getAccessGroupsForCurrentUser(agResponse);
                const accessGroupCache = new SessionStoreCache(groupsForUser.map(gr => gr.name));
                accessGroupCache.writeToSessionStorage(sessionStorageAccessGroupsKey);

                return accessGroupCache.values;
            } catch (e) {
                const accessGroupCache = new SessionStoreCache([], -1); // set to expired to trigger a retry on next page load
                accessGroupCache.writeToSessionStorage(sessionStorageAccessGroupsKey);

                return accessGroupCache.values;
            }
        }

        return accessGroups.values;
    }

    /**
     * Filters and maps AccessGroups a users has, before stored in sessionStorage.
     *
     */
    getAccessGroupsForCurrentUser(accessGroupArray: AccessGroup[]): AccessGroup[] {
        const user = this.getUser();
        if (!user) {
            throw new Error('Failed to retrieve account info');
        }
        if (!user.idTokenClaims) {
            throw new Error('Failed to retrieve claims from idToken');
        }
        const userRoles = user.idTokenClaims.roles ?? [];

        return accessGroupArray
            .filter(g => userRoles.includes(g.policy));
    }

    /**
     * Clears the users attributes from the API cache
     */
    async refreshUserAttributes(): Promise<void> {
        await clearAttributesApiCache(this);
    }

    /**
     * Evaluates if user has the requested permission
     */
    async hasPermission(permission: string): Promise<boolean> {
        const permissions = await this.getPermissions();
        return permissions.includes(permission);
    }

    /**
     * Evaluates if the user has ANY of the requested permissions.
     */
    async hasAnyPermission(requiredPermissions: string[]): Promise<boolean> {
        const permissions = await this.getPermissions();
        return _.intersection(requiredPermissions, permissions).length > 0;
    }
}

export default AuthService;
